import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Box, Button, Paper, TextField, Typography, Alert, Stack, Link,
  InputAdornment, CircularProgress, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import { useAuth } from "../auth/AuthContext";
import { homePathForRole, Role } from "../auth/roles";
import { register, RegisterUserType, resolveTenantBySlug } from "../data/api";
import { BRAND } from "../theme";

const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const RegisterPage: React.FC = () => {
  const { login, setRole } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [userType, setUserType] = useState<RegisterUserType>("BUYER");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const distributorSlug = params.get("d") || params.get("distributor") || "";
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [tenantName, setTenantName] = useState<string>("");

  // Phase C: binding a buyer to the distributor whose ordering URL they used.
  useEffect(() => {
    if (!distributorSlug) return;
    (async () => {
      const t = await resolveTenantBySlug(distributorSlug);
      if (t) { setTenantId(t.id); setTenantName(t.name); }
    })();
  }, [distributorSlug]);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  const validate = (): string | null => {
    if (!f.name.trim()) return "Please enter your business or contact name.";
    if (!emailOk(f.email)) return "Please enter a valid email address.";
    if (f.password.length < 6) return "Password must be at least 6 characters.";
    if (f.password !== f.confirm) return "Passwords do not match.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setLoading(true);
    try {
      await register({
        name: f.name.trim(),
        email: f.email.trim(),
        password: f.password,
        phone: f.phone.trim(),
        userType,
        tenantId,
      });
      // A seller is a distributor-side account; a buyer is a customer account.
      // The backend User model has no role column, so the sign-up choice is
      // authoritative on the client.
      const chosenRole: Role = userType === "SELLER" ? "DISTRIBUTOR" : "BUYER";
      // Try to sign in immediately; fall back to the login screen if the
      // backend expects a different username field.
      try {
        await login(f.email.trim(), f.password);
        // Override the token-derived role with the buyer/seller choice.
        setRole(chosenRole);
        navigate(homePathForRole(chosenRole), { replace: true });
      } catch {
        // Remember the chosen portal so routing is correct once they log in.
        setRole(chosenRole);
        navigate("/login", { replace: true, state: { registered: true } });
      }
    } catch {
      setError("Couldn't create your account. The email may already be in use, or the backend is unreachable.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2,
      background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)` }}>
      <Paper elevation={0} sx={{ width: "100%", maxWidth: 460, p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box sx={{ bgcolor: BRAND.greenSoft, color: BRAND.green, width: 56, height: 56, borderRadius: 2, display: "grid", placeItems: "center" }}>
            <StorefrontIcon fontSize="large" />
          </Box>
          <Typography variant="h5">Create your account</Typography>
          <Typography variant="body2" color="text.secondary">Join the Food Wholesale hub</Typography>
        </Stack>

        {tenantName && <Alert severity="info" sx={{ mb: 2 }}>Registering under <strong>{tenantName}</strong>.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            I want to join as
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            color="primary"
            value={userType}
            onChange={(_e, val: RegisterUserType | null) => { if (val) setUserType(val); }}
            sx={{ mb: 1 }}
          >
            <ToggleButton value="BUYER" sx={{ py: 1.2, textTransform: "none", gap: 1 }}>
              <ShoppingBagOutlinedIcon fontSize="small" /> Buyer
            </ToggleButton>
            <ToggleButton value="SELLER" sx={{ py: 1.2, textTransform: "none", gap: 1 }}>
              <StoreOutlinedIcon fontSize="small" /> Seller
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField fullWidth label="Business / contact name" margin="normal" value={f.name} onChange={set("name")} required autoFocus
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon fontSize="small" /></InputAdornment> }} />
          <TextField fullWidth label="Email" type="email" margin="normal" value={f.email} onChange={set("email")} required
            InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineIcon fontSize="small" /></InputAdornment> }} />
          <TextField fullWidth label="Phone (optional)" margin="normal" value={f.phone} onChange={set("phone")}
            InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIphoneIcon fontSize="small" /></InputAdornment> }} />
          <TextField fullWidth label="Password" type="password" margin="normal" value={f.password} onChange={set("password")} required
            helperText="At least 6 characters"
            InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment> }} />
          <TextField fullWidth label="Confirm password" type="password" margin="normal" value={f.confirm} onChange={set("confirm")} required
            InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment> }} />

          <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 2.5, py: 1.2 }}>
            {loading ? <CircularProgress size={22} color="inherit" /> : "Create account"}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Already have an account?{" "}
          <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>Sign in</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default RegisterPage;
